import { useEffect, useMemo, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { adminService } from "../../features/admin/service";
import type {
  OrgCandidateCluster,
  OrgCandidateUser,
  OrgMatchField,
} from "../../features/admin/types";

const MATCH_FIELD_LABELS: Record<OrgMatchField, string> = {
  gst_number: "GST number",
  business_registration_id: "Business registration ID",
  agency_name: "Agency name",
  company_email_domain: "Company email domain",
  website: "Website",
};

// GST is the strongest signal (exact, government-issued); free-text agency name
// is the noisiest ("Pvt Ltd" vs "Private Limited", typos). Colour-code the
// cluster badge so superadmin reads confidence at a glance before merging.
const MATCH_FIELD_COLORS: Record<OrgMatchField, string> = {
  gst_number: "bg-green-500/10 text-green-400",
  business_registration_id: "bg-green-500/10 text-green-400",
  company_email_domain: "bg-amber-500/10 text-amber-400",
  website: "bg-amber-500/10 text-amber-400",
  agency_name: "bg-gray-500/10 text-gray-400",
};

type SearchFilters = {
  agency_name: string;
  gst_number: string;
  business_registration_id: string;
  company_email_domain: string;
  website: string;
};

const EMPTY_FILTERS: SearchFilters = {
  agency_name: "",
  gst_number: "",
  business_registration_id: "",
  company_email_domain: "",
  website: "",
};

/** One search field with a live "who already matches this?" dropdown — pulled
 *  from the same clustering search, filtered to this field's own clusters, so
 *  superadmin picks a value real users already share instead of typing blind. */
const SuggestField: React.FC<{
  field: keyof SearchFilters;
  matchField: OrgMatchField;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ field: _field, matchField, label, placeholder, value, onChange }) => {
  const [suggestions, setSuggestions] = useState<{ value: string; count: number }[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await adminService.searchOrgCandidates({ [matchField]: q } as any);
        setSuggestions(
          data.clusters
            .filter((c) => c.match_field === matchField)
            .map((c) => ({ value: c.match_value, count: c.users.length })),
        );
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, matchField]);

  return (
    <div className="relative">
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
      />
      {open && value.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {loading ? (
            <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No existing users share this value yet.</div>
          ) : (
            suggestions.map((s) => (
              <button
                key={s.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(s.value); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors border-b border-gray-800 last:border-0"
              >
                <span className="text-xs text-white break-all">{s.value}</span>
                <span className="text-[11px] text-gray-500 ml-2">{s.count} users</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface CreateOrgModalProps {
  cluster: OrgCandidateCluster;
  onClose: () => void;
  onCreated: (orgName: string) => void;
}

/** Step 2 of the flow: superadmin has picked a cluster and now names the org,
 *  designates one or more Org Admins (active immediately) and trims the rest
 *  down to the candidate pool the Org Admins will be allowed to invite from. */
const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ cluster, onClose, onCreated }) => {
  useBodyScrollLock();
  // A user already attached to an org can never join a second one (the backend
  // 409s), so those rows are read-only here.
  const selectable = useMemo(
    () => cluster.users.filter((u) => !u.organization?.org_id),
    [cluster.users],
  );
  const attached = useMemo(
    () => cluster.users.filter((u) => u.organization?.org_id),
    [cluster.users],
  );

  const [name, setName] = useState(
    cluster.match_field === "agency_name" ? cluster.match_value : "",
  );
  const [website, setWebsite] = useState(
    cluster.match_field === "website" ? cluster.match_value : "",
  );
  const [seatLimit, setSeatLimit] = useState("");
  const [orgAdminUserIds, setOrgAdminUserIds] = useState<string[]>(
    selectable[0] ? [selectable[0].user_id] : [],
  );
  const [candidateIds, setCandidateIds] = useState<string[]>(selectable.map((u) => u.user_id));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleCandidate = (userId: string) => {
    setCandidateIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const toggleOrgAdmin = (userId: string) => {
    setOrgAdminUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const poolIds = candidateIds.filter((id) => !orgAdminUserIds.includes(id));

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError("Organization name is required.");
      return;
    }
    if (orgAdminUserIds.length === 0) {
      setError("Pick at least one user to be an Org Admin.");
      return;
    }
    setSubmitting(true);
    try {
      // The cluster was matched on a real company signal — seed the org's
      // identity fields from it so they don't have to be retyped.
      const gstNumbers = cluster.match_field === "gst_number" ? [cluster.match_value] : [];
      const businessRegIds =
        cluster.match_field === "business_registration_id" ? [cluster.match_value] : [];
      await adminService.createOrganization({
        name: name.trim(),
        org_admin_user_ids: orgAdminUserIds,
        candidate_user_ids: poolIds,
        seat_limit: seatLimit ? Number(seatLimit) : null,
        website: website.trim() || null,
        canonical_agency_name:
          cluster.match_field === "agency_name" ? cluster.match_value : name.trim(),
        gst_numbers: gstNumbers,
        business_registration_ids: businessRegIds,
        // Audit trail of what this org was actually matched on.
        match_criteria: { match_field: cluster.match_field, match_value: cluster.match_value },
      });
      onCreated(name.trim());
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create organization.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white">Create organization from cluster</h2>
            <p className="text-sm text-gray-400 mt-0.5 break-words">
              Matched on {MATCH_FIELD_LABELS[cluster.match_field]}: <span className="text-gray-200">{cluster.match_value}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-sm shrink-0">Close</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="sm:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">Organization name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Travels Pvt Ltd"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Website (optional)</label>
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="acmetravels.com"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Seat limit (optional)</label>
            <input
              type="number"
              min={1}
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              placeholder="Platform default"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-2">
          Every Org Admin you tick becomes active immediately. Everyone else you keep ticked becomes a
          <span className="text-gray-300"> candidate</span> — an Org Admin can invite them, but can never add anyone outside this pool.
        </p>

        <div className="border border-gray-800 rounded-xl overflow-hidden mb-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 bg-gray-900/60">
                  <th className="text-left py-2.5 px-3 text-gray-500 font-medium">In pool</th>
                  <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Org Admin</th>
                  <th className="text-left py-2.5 px-3 text-gray-500 font-medium">User</th>
                  <th className="text-left py-2.5 px-3 text-gray-500 font-medium">Agency</th>
                  <th className="text-left py-2.5 px-3 text-gray-500 font-medium">GST</th>
                </tr>
              </thead>
              <tbody>
                {selectable.map((user) => {
                  const isAdmin = orgAdminUserIds.includes(user.user_id);
                  return (
                    <tr key={user.user_id} className="border-b border-gray-800/50">
                      <td className="py-2.5 px-3">
                        <input
                          type="checkbox"
                          checked={isAdmin || candidateIds.includes(user.user_id)}
                          disabled={isAdmin}
                          onChange={() => toggleCandidate(user.user_id)}
                          className="accent-indigo-500"
                          aria-label={`Include ${user.email || user.user_id} in the candidate pool`}
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <input
                          type="checkbox"
                          checked={isAdmin}
                          onChange={() => toggleOrgAdmin(user.user_id)}
                          className="accent-indigo-500"
                          aria-label={`Make ${user.email || user.user_id} an org admin`}
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="text-gray-200">{user.name || "Unnamed user"}</div>
                        <div className="text-xs text-gray-500">{user.email} · {user.role}</div>
                      </td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{user.agency_name || "—"}</td>
                      <td className="py-2.5 px-3 text-gray-400 text-xs">{user.gst_number || "—"}</td>
                    </tr>
                  );
                })}
                {attached.map((user) => (
                  <tr key={user.user_id} className="border-b border-gray-800/50 opacity-60">
                    <td className="py-2.5 px-3 text-xs text-gray-600" colSpan={2}>Unavailable</td>
                    <td className="py-2.5 px-3">
                      <div className="text-gray-300">{user.name || "Unnamed user"}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-500" colSpan={2}>
                      Already in “{user.organization?.org_name}” ({user.organization?.status})
                    </td>
                  </tr>
                ))}
                {selectable.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500 text-sm">
                      Every user in this cluster already belongs to an organization.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 rounded-md p-3 mb-4 break-words">{error}</div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {orgAdminUserIds.length} org admin{orgAdminUserIds.length === 1 ? "" : "s"} + {poolIds.length} candidate{poolIds.length === 1 ? "" : "s"}
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-md transition-colors">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || selectable.length === 0}
              className="px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating…" : `Create org from these ${poolIds.length + orgAdminUserIds.length} users`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CandidateRow: React.FC<{ user: OrgCandidateUser }> = ({ user }) => (
  <tr className="border-b border-gray-800/50">
    <td className="py-3 px-4">
      <div className="text-gray-200">{user.name || "Unnamed user"}</div>
      <div className="text-xs text-gray-500">{user.email}</div>
    </td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.role || "—"}</td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.agency_name || "—"}</td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.gst_number || "—"}</td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.gst_trade_name || user.gst_legal_name || "—"}</td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.company_email || "—"}</td>
    <td className="py-3 px-4 text-gray-400 text-xs">{user.website || "—"}</td>
    <td className="py-3 px-4 text-xs">
      {user.organization?.org_id ? (
        <span className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-400">
          {user.organization.org_name} · {user.organization.status}
        </span>
      ) : (
        <span className="text-gray-600">Unattached</span>
      )}
    </td>
  </tr>
);

const AdminOrgClusteringPage: React.FC = () => {
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
  const [clusters, setClusters] = useState<OrgCandidateCluster[] | null>(null);
  const [totalMatched, setTotalMatched] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeCluster, setActiveCluster] = useState<OrgCandidateCluster | null>(null);
  const { alertModal, showAlert } = useAlertModal();

  const hasFilter = Object.values(filters).some((v) => v.trim());

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!hasFilter) return;
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]),
      );
      const data = await adminService.searchOrgCandidates(params);
      setClusters(data.clusters);
      setTotalMatched(data.total_users_matched);
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to search for candidate clusters.");
    } finally {
      setLoading(false);
    }
  };

  const setFilter = (key: keyof SearchFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <div>
        <AdminPageHeader
          title="Organization Clustering"
          subtitle="Find existing buyer/seller logins that belong to the same real company, then create an organization from that cluster. Nothing is ever merged automatically."
        />

        <form onSubmit={handleSearch} className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <SuggestField
              field="agency_name"
              matchField="agency_name"
              label="Agency name (fuzzy)"
              placeholder="acme travels"
              value={filters.agency_name}
              onChange={(v) => setFilter("agency_name", v)}
            />
            <SuggestField
              field="gst_number"
              matchField="gst_number"
              label="GST number (exact)"
              placeholder="27AAAAA0000A1Z5"
              value={filters.gst_number}
              onChange={(v) => setFilter("gst_number", v)}
            />
            <SuggestField
              field="business_registration_id"
              matchField="business_registration_id"
              label="Business registration ID"
              placeholder="U63040MH2010PTC000000"
              value={filters.business_registration_id}
              onChange={(v) => setFilter("business_registration_id", v)}
            />
            <SuggestField
              field="company_email_domain"
              matchField="company_email_domain"
              label="Company email domain"
              placeholder="acmetravels.com"
              value={filters.company_email_domain}
              onChange={(v) => setFilter("company_email_domain", v)}
            />
            <SuggestField
              field="website"
              matchField="website"
              label="Website"
              placeholder="acmetravels.com"
              value={filters.website}
              onChange={(v) => setFilter("website", v)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button
              type="submit"
              disabled={loading || !hasFilter}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? "Searching…" : "Search"}
            </button>
            <button
              type="button"
              onClick={() => { setFilters(EMPTY_FILTERS); setClusters(null); setTotalMatched(0); }}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm transition-colors"
            >
              Clear
            </button>
            <span className="text-xs text-gray-500">
              Any field matches on its own — GST is the strongest signal, free-text agency name the noisiest.
            </span>
          </div>
        </form>

        {loading ? (
          <div className="py-20 flex justify-center"><Spinner /></div>
        ) : clusters === null ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl py-16 text-center text-gray-500 text-sm">
            Enter at least one company signal above to look for clusters.
          </div>
        ) : clusters.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl py-16 text-center text-gray-500 text-sm">
            {totalMatched === 0
              ? "No users matched those signals."
              : `${totalMatched} user${totalMatched === 1 ? "" : "s"} matched, but no two of them share a company signal — a cluster needs at least 2 users.`}
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-gray-500">
              {clusters.length} cluster{clusters.length === 1 ? "" : "s"} across {totalMatched} matched user{totalMatched === 1 ? "" : "s"}.
              A user can appear in more than one cluster — pick the one that is the real company.
            </p>
            {clusters.map((cluster) => (
              <div key={`${cluster.match_field}:${cluster.match_value}`} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-800">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${MATCH_FIELD_COLORS[cluster.match_field]}`}>
                        {MATCH_FIELD_LABELS[cluster.match_field]}
                      </span>
                      <span className="text-white font-medium break-all">{cluster.match_value}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{cluster.users.length} users share this value</p>
                  </div>
                  <button
                    onClick={() => setActiveCluster(cluster)}
                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
                  >
                    Create org from these {cluster.users.length} users
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Agency name</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">GST</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">GST trade/legal name</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Company email</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Website</th>
                        <th className="text-left py-3 px-4 text-gray-500 font-medium">Existing org</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cluster.users.map((user) => (
                        <CandidateRow key={user.user_id} user={user} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeCluster && (
        <CreateOrgModal
          cluster={activeCluster}
          onClose={() => setActiveCluster(null)}
          onCreated={(orgName) => {
            setActiveCluster(null);
            // Re-run the search so the just-attached users now show their new
            // org badge instead of "Unattached" (they can't be clustered again).
            handleSearch();
            showAlert(
              `“${orgName}” created. The designated Org Admins can sign in at /org-admin/login now, and the rest of the cluster is waiting in their candidate pool.`,
              { title: "Organization created", variant: "info" },
            );
          }}
        />
      )}
      {alertModal}
    </>
  );
};

export default AdminOrgClusteringPage;
